/**
 * 本机摄像头刷脸会话（与萤石监控流严格分离）
 *
 * 开盘 start → 暖机/采帧/质量门 → mock 或真识别 → ready/failed
 * 选槽 awaitFaceResult 合流；关盘 cancel 停流
 *
 * @see docs/surveillance-去向登记无感刷脸设计.md
 * @see docs/surveillance-人脸识别开发步骤.md（P3）
 */

import { loadFaceLocalConfig, recognizeFace } from '@/service/face'

/** 监控页当前门店（List 同步 effectiveStoreId；识别/登记按店隔离） */
let pageStoreId = ''

/**
 * @param {string|number|null|undefined} storeId
 */
export function setFacePageStoreId (storeId) {
  pageStoreId = storeId === undefined || storeId === null ? '' : String(storeId).trim()
}

/** @returns {string} */
export function getFacePageStoreId () {
  return pageStoreId
}

/** 本地演示用医护名单（仅 mock） */
export const MOCK_NURSE_OPTIONS = [
  { id: 'N001', name: '王护士' },
  { id: 'N002', name: '李护士' },
  { id: 'N003', name: '张护士' }
]

/** 可调常量（现场可改；face-local.json 可覆盖 useMockRecognition / baseUrl） */
export const FACE_CAPTURE_CONFIG = {
  /** 开流后短暖机，避免首帧过暗/未稳 */
  warmUpMs: 80,
  /** 采帧上限；凑够帧+清晰即可提前交 */
  captureWindowMs: 700,
  /** 至少采这么久再允许「够清晰就交」 */
  captureMinMs: 280,
  /** 被动活体最少帧数 */
  livenessMinFrames: 3,
  /** 活体序列最多保留帧数 */
  livenessMaxFrames: 5,
  /** 活体序列采样间隔（ms） */
  livenessSampleIntervalMs: 100,
  /** 达到该清晰度可提前交帧（相对 sharpnessThreshold）；须已凑够活体帧 */
  earlyCaptureSharpnessFactor: 2,
  /** 可恢复失败后立刻重试前的短暂停顿 */
  retryPauseMs: 200,
  /**
   * 单次开识别最多自动重试次数；耗尽后落 failed（识别错误，没有此人）
   * 点取景框 startFaceSession 会重新计数
   */
  maxRecognizeAttempts: 10,
  /** 选去向时仍 recognizing 的短等（原 3s，缩短 2s） */
  awaitMergeMs: 1000,
  sessionTimeoutMs: 6000,
  /** Laplacian 清晰度阈值；过低易误杀，一期偏松 */
  sharpnessThreshold: 12,
  /**
   * true：本地 mock 名单；false：调本店 /face/api/recognize
   * face-local.json 的 useMockRecognition 优先
   */
  useMockRecognition: false,
  /** 真识别服务根路径（可被 face-local.json.baseUrl 覆盖） */
  faceBaseUrl: '/face',
  /**
   * mock 失败率 0～1；默认 0 走无感正流程
   * 可恢复失败会自动重试，最多 maxRecognizeAttempts 次后 failed
   */
  mockFailRate: 0,
  /** 上传帧最长边，控制体积 */
  uploadMaxSide: 640,
  uploadJpegQuality: 0.82,
  video: {
    facingMode: 'user',
    width: { ideal: 1280 },
    height: { ideal: 720 }
  }
}

export const FACE_SESSION_STATUS = {
  IDLE: 'idle',
  REQUESTING_PERMISSION: 'requesting_permission',
  RECOGNIZING: 'recognizing',
  READY: 'ready',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
}

/** 暂时测试：未真正识别时也返回通过，便于提交去向。测完改回 false。 */
export const FACE_RESULT_ALWAYS_OK = false

/** 1×1 PNG，仅用于测试提交时的抓拍文件 */
const TEST_FACE_PNG = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg=='

function testPassFaceResult () {
  const binary = atob(TEST_FACE_PNG)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return {
    ok: true,
    nurseId: '4733',
    nurseName: '测试',
    nurseAvatar: new Blob([bytes], { type: 'image/png' }),
    mock: true
  }
}

/** 可恢复 reason：继续采帧重试，不固定 failed */
export const FACE_RECOVERABLE_REASONS = {
  no_face: true,
  mismatch: true,
  liveness_fail: true,
  quality: true,
  timeout: true,
  service_error: true,
  bad_image: true,
  retrying: true
}

/** @type {Map<string, FaceSession>} */
const sessions = new Map()

function pickMockNurse () {
  const idx = Math.floor(Math.random() * MOCK_NURSE_OPTIONS.length)
  return MOCK_NURSE_OPTIONS[idx] || MOCK_NURSE_OPTIONS[0]
}

function delay (ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(Object.assign(new Error('aborted'), { name: 'AbortError' }))
      return
    }
    const timer = window.setTimeout(resolve, ms)
    const onAbort = () => {
      window.clearTimeout(timer)
      reject(Object.assign(new Error('aborted'), { name: 'AbortError' }))
    }
    if (signal) {
      signal.addEventListener('abort', onAbort, { once: true })
    }
  })
}

function stopStream (stream) {
  if (!stream) return
  stream.getTracks().forEach((track) => {
    try {
      track.stop()
    } catch (e) {
      /* ignore */
    }
  })
}

/**
 * 简易清晰度：灰度 Laplacian 方差（值越大越清晰）
 * @param {ImageData} imageData
 */
function computeSharpness (imageData) {
  const { data, width, height } = imageData
  if (!width || !height || width < 3 || height < 3) return 0
  let sum = 0
  let sumSq = 0
  let count = 0
  // 抽样步长，降低 CPU
  const step = Math.max(1, Math.floor(Math.min(width, height) / 80))
  for (let y = 1; y < height - 1; y += step) {
    for (let x = 1; x < width - 1; x += step) {
      const i = (y * width + x) * 4
      const c = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
      const up = ((y - 1) * width + x) * 4
      const down = ((y + 1) * width + x) * 4
      const left = (y * width + (x - 1)) * 4
      const right = (y * width + (x + 1)) * 4
      const u = 0.299 * data[up] + 0.587 * data[up + 1] + 0.114 * data[up + 2]
      const d = 0.299 * data[down] + 0.587 * data[down + 1] + 0.114 * data[down + 2]
      const l = 0.299 * data[left] + 0.587 * data[left + 1] + 0.114 * data[left + 2]
      const r = 0.299 * data[right] + 0.587 * data[right + 1] + 0.114 * data[right + 2]
      const lap = Math.abs(4 * c - u - d - l - r)
      sum += lap
      sumSq += lap * lap
      count += 1
    }
  }
  if (!count) return 0
  const mean = sum / count
  return sumSq / count - mean * mean
}

function sampleVideoSharpness (video, canvas) {
  const w = video.videoWidth
  const h = video.videoHeight
  if (!w || !h) return 0
  const maxSide = 320
  const scale = Math.min(1, maxSide / Math.max(w, h))
  const cw = Math.max(2, Math.floor(w * scale))
  const ch = Math.max(2, Math.floor(h * scale))
  canvas.width = cw
  canvas.height = ch
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return 0
  ctx.drawImage(video, 0, 0, cw, ch)
  try {
    return computeSharpness(ctx.getImageData(0, 0, cw, ch))
  } catch (e) {
    return 0
  }
}

/**
 * 从 video 抓一帧 JPEG（用于 /api/recognize）
 * @returns {Promise<Blob|null>}
 */
function captureVideoJpeg (video, canvas) {
  const w = video.videoWidth
  const h = video.videoHeight
  if (!w || !h) return Promise.resolve(null)
  const maxSide = FACE_CAPTURE_CONFIG.uploadMaxSide || 720
  const scale = Math.min(1, maxSide / Math.max(w, h))
  const cw = Math.max(2, Math.floor(w * scale))
  const ch = Math.max(2, Math.floor(h * scale))
  canvas.width = cw
  canvas.height = ch
  const ctx = canvas.getContext('2d')
  if (!ctx) return Promise.resolve(null)
  ctx.drawImage(video, 0, 0, cw, ch)
  const quality = FACE_CAPTURE_CONFIG.uploadJpegQuality || 0.9
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob || null),
      'image/jpeg',
      quality
    )
  })
}

async function shouldUseMockRecognition () {
  const local = await loadFaceLocalConfig()
  if (typeof local.useMockRecognition === 'boolean') {
    return local.useMockRecognition
  }
  return Boolean(FACE_CAPTURE_CONFIG.useMockRecognition)
}

/**
 * @returns {Promise<
 *   | { ok: true, nurseId: string, nurseName: string, mock: boolean }
 *   | { ok: false, reason: string }
 * >}
 */
async function recognizeFromBestFrame (blob, frames, signal) {
  if (!blob) {
    return { ok: false, reason: 'quality' }
  }
  const storeId = getFacePageStoreId()
  if (!storeId) {
    return { ok: false, reason: 'service_error' }
  }
  const local = await loadFaceLocalConfig()
  const result = await recognizeFace({
    blob,
    frames,
    storeId,
    signal,
    baseUrl: local.baseUrl || FACE_CAPTURE_CONFIG.faceBaseUrl,
    apiKey: local.apiKey
  })
  if (result.ok) {
    return {
      ok: true,
      nurseId: result.nurseId,
      nurseName: result.nurseName,
      mock: false
    }
  }
  return { ok: false, reason: result.reason || 'mismatch' }
}

/**
 * @typedef {{
 *   nurseId: string,
 *   nurseName: string,
 *   nurseAvatar: Blob|null,
 *   mock: boolean
 * }} FaceVerifyResult
 */

/**
 * @typedef {{
 *   status: string,
 *   reason: string|null,
 *   result: FaceVerifyResult|null,
 *   streamActive: boolean
 * }} FaceSessionSnapshot
 */

/**
 * @typedef {{
 *   cellKey: string,
 *   status: string,
 *   reason: string|null,
 *   result: FaceVerifyResult|null,
 *   generation: number,
 *   abortController: AbortController,
 *   stream: MediaStream|null,
 *   waiters: Array<(s: FaceSession) => void>,
 *   listeners: Array<(snap: FaceSessionSnapshot) => void>
 * }} FaceSession
 */

function toSnapshot (session) {
  return {
    status: session.status,
    reason: session.reason,
    result: session.result,
    streamActive: Boolean(session.stream)
  }
}

function idleSnapshot () {
  return {
    status: FACE_SESSION_STATUS.IDLE,
    reason: null,
    result: null,
    streamActive: false
  }
}

function releaseSessionStream (session) {
  if (!session.stream) return
  stopStream(session.stream)
  session.stream = null
  notifyListeners(session)
}

/** 一次性等待者（awaitFaceResult）：通知后清空 */
function notifyWaiters (session) {
  const list = session.waiters.splice(0, session.waiters.length)
  list.forEach((fn) => {
    try {
      fn(session)
    } catch (e) {
      /* ignore */
    }
  })
}

/** UI 订阅者：不清空，每次状态变更都推送快照 */
function notifyListeners (session) {
  const snap = toSnapshot(session)
  session.listeners.forEach((fn) => {
    try {
      fn(snap)
    } catch (e) {
      /* ignore */
    }
  })
}

function setSessionStatus (session, status, patch = {}) {
  session.status = status
  if (patch.reason !== undefined) session.reason = patch.reason
  if (patch.result !== undefined) session.result = patch.result
  notifyWaiters(session)
  notifyListeners(session)
}

function createIdleSession (cellKey) {
  return {
    cellKey,
    status: FACE_SESSION_STATUS.IDLE,
    reason: null,
    result: null,
    generation: 0,
    abortController: new AbortController(),
    stream: null,
    waiters: [],
    listeners: []
  }
}

/**
 * QQ / 旧内核：mediaDevices.getUserMedia 可能缺失，回退到带前缀 API。
 * @returns {MediaDevices|null}
 */
function ensureMediaDevices () {
  if (typeof navigator === 'undefined') return null
  if (!navigator.mediaDevices) {
    try {
      navigator.mediaDevices = {}
    } catch (e) {
      return null
    }
  }
  if (typeof navigator.mediaDevices.getUserMedia === 'function') {
    return navigator.mediaDevices
  }
  const legacy = navigator.getUserMedia
    || navigator.webkitGetUserMedia
    || navigator.mozGetUserMedia
    || navigator.msGetUserMedia
  if (typeof legacy !== 'function') return null
  navigator.mediaDevices.getUserMedia = (constraints) => new Promise((resolve, reject) => {
    legacy.call(navigator, constraints, resolve, reject)
  })
  return navigator.mediaDevices
}

/**
 * 逐级放宽约束申请摄像头。
 * QQ 等浏览器对 facingMode / 高分辨率易 OverconstrainedError。
 * @param {MediaTrackConstraints|boolean} [videoConstraints]
 * @returns {Promise<MediaStream>}
 */
export async function acquireFaceStream (videoConstraints) {
  const mediaDevices = ensureMediaDevices()
  if (!mediaDevices) {
    const err = new Error('getUserMedia is not implemented')
    err.name = 'NotSupportedError'
    throw err
  }
  // 非安全上下文（非 https / 非 localhost）多数浏览器直接禁摄像头
  if (
    typeof window !== 'undefined'
    && window.isSecureContext === false
  ) {
    const err = new Error('Camera requires secure context (HTTPS or localhost)')
    err.name = 'SecurityError'
    throw err
  }

  const preferred = videoConstraints != null
    ? videoConstraints
    : FACE_CAPTURE_CONFIG.video
  const attempts = [
    { audio: false, video: preferred },
    { audio: false, video: { facingMode: 'user' } },
    { audio: false, video: true }
  ]
  let lastErr = null
  for (let i = 0; i < attempts.length; i += 1) {
    try {
      return await mediaDevices.getUserMedia(attempts[i])
    } catch (err) {
      lastErr = err
      const name = err && err.name
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        throw err
      }
    }
  }
  throw lastErr || new Error('getUserMedia failed')
}

async function runCapturePipeline (session) {
  const {
    warmUpMs,
    captureWindowMs,
    captureMinMs,
    earlyCaptureSharpnessFactor,
    retryPauseMs,
    maxRecognizeAttempts,
    sharpnessThreshold,
    mockFailRate,
    livenessMinFrames,
    livenessMaxFrames,
    livenessSampleIntervalMs,
    video: videoConstraints
  } = FACE_CAPTURE_CONFIG
  const signal = session.abortController.signal
  const generation = session.generation
  const useMock = await shouldUseMockRecognition()
  const earlySharpness = sharpnessThreshold * (
    Number.isFinite(earlyCaptureSharpnessFactor) ? earlyCaptureSharpnessFactor : 2.2
  )
  const minCaptureMs = Number.isFinite(captureMinMs) ? captureMinMs : 500
  const minLiveFrames = Number.isFinite(livenessMinFrames) ? livenessMinFrames : 3
  const maxLiveFrames = Number.isFinite(livenessMaxFrames) ? livenessMaxFrames : 5
  const sampleIntervalMs = Number.isFinite(livenessSampleIntervalMs)
    ? livenessSampleIntervalMs
    : 120
  const maxAttempts = Number.isFinite(maxRecognizeAttempts) && maxRecognizeAttempts > 0
    ? maxRecognizeAttempts
    : 10

  const isStale = () =>
    signal.aborted
    || sessions.get(session.cellKey) !== session
    || session.generation !== generation

  setSessionStatus(session, FACE_SESSION_STATUS.REQUESTING_PERMISSION)

  let stream = null
  try {
    stream = await acquireFaceStream(videoConstraints)
  } catch (err) {
    if (isStale()) return
    const name = err && err.name
    const reason = name === 'NotAllowedError' || name === 'PermissionDeniedError'
      ? 'permission'
      : 'device'
    setSessionStatus(session, FACE_SESSION_STATUS.FAILED, { reason, result: null })
    return
  }

  if (isStale()) {
    stopStream(stream)
    return
  }

  session.stream = stream
  setSessionStatus(session, FACE_SESSION_STATUS.RECOGNIZING)

  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.srcObject = stream

  const abortToCancel = () => {
    releaseSessionStream(session)
    video.srcObject = null
    if (!isStale() && session.status !== FACE_SESSION_STATUS.CANCELLED) {
      setSessionStatus(session, FACE_SESSION_STATUS.CANCELLED, {
        reason: 'cancelled',
        result: null
      })
    }
  }

  const failAfterMaxAttempts = () => {
    setSessionStatus(session, FACE_SESSION_STATUS.FAILED, {
      reason: 'not_found',
      result: null
    })
    video.srcObject = null
    releaseSessionStream(session)
  }

  try {
    await video.play()
  } catch (e) {
    /* 部分浏览器 play 失败仍可读帧 */
  }

  const readyStarted = Date.now()
  while (!video.videoWidth && Date.now() - readyStarted < 2000) {
    if (isStale()) break
    try {
      await delay(50, signal)
    } catch (e) {
      break
    }
  }

  const sharpnessCanvas = document.createElement('canvas')
  const uploadCanvas = document.createElement('canvas')
  let attempt = 0

  while (!isStale()) {
    attempt += 1
    const warmMs = attempt === 1 ? warmUpMs : Math.min(160, warmUpMs)

    try {
      await delay(warmMs, signal)
    } catch (e) {
      abortToCancel()
      return
    }

    if (isStale()) {
      releaseSessionStream(session)
      video.srcObject = null
      return
    }

    if (session.status !== FACE_SESSION_STATUS.RECOGNIZING) {
      setSessionStatus(session, FACE_SESSION_STATUS.RECOGNIZING, {
        reason: null,
        result: null
      })
    }

    let bestSharpness = 0
    let bestBlob = null
    /** @type {Blob[]} */
    const liveFrames = []
    let lastSampleAt = 0
    const captureStarted = Date.now()
    while (Date.now() - captureStarted < captureWindowMs) {
      if (isStale()) break
      const score = sampleVideoSharpness(video, sharpnessCanvas)
      const now = Date.now()
      const shouldSample = liveFrames.length === 0
        || now - lastSampleAt >= sampleIntervalMs
      if (shouldSample && liveFrames.length < maxLiveFrames) {
        // eslint-disable-next-line no-await-in-loop
        const frameBlob = await captureVideoJpeg(video, uploadCanvas)
        if (frameBlob) {
          liveFrames.push(frameBlob)
          lastSampleAt = now
          if (score > bestSharpness) {
            bestSharpness = score
            bestBlob = frameBlob
          }
        }
      } else if (score > bestSharpness) {
        bestSharpness = score
        // eslint-disable-next-line no-await-in-loop
        bestBlob = await captureVideoJpeg(video, uploadCanvas)
      }
      // 须凑够活体帧 + 最短时长 + 清晰度，才允许提前交帧
      if (
        bestBlob
        && liveFrames.length >= minLiveFrames
        && bestSharpness >= earlySharpness
        && Date.now() - captureStarted >= minCaptureMs
      ) {
        break
      }
      try {
        await delay(50, signal)
      } catch (e) {
        break
      }
    }

    if (isStale()) {
      abortToCancel()
      return
    }

    // 清晰度软门：过低则当 quality 可恢复重试
    if (bestSharpness < sharpnessThreshold && !useMock) {
      if (attempt >= maxAttempts) {
        failAfterMaxAttempts()
        return
      }
      setSessionStatus(session, FACE_SESSION_STATUS.RECOGNIZING, {
        reason: 'quality',
        result: null
      })
      try {
        await delay(retryPauseMs, signal)
      } catch (e) {
        abortToCancel()
        return
      }
      continue
    }

    let outcome
    if (useMock) {
      const failRate = Number(mockFailRate)
      const mockFailed = Number.isFinite(failRate) && failRate > 0 && Math.random() < failRate
      if (mockFailed) {
        outcome = { ok: false, reason: 'retrying' }
      } else {
        const nurse = pickMockNurse()
        outcome = {
          ok: true,
          nurseId: nurse.id,
          nurseName: nurse.name,
          mock: true
        }
      }
    } else {
      const framesForLive = liveFrames.length > 0
        ? liveFrames
        : (bestBlob ? [bestBlob] : [])
      outcome = await recognizeFromBestFrame(bestBlob, framesForLive, signal)
    }

    if (isStale()) {
      abortToCancel()
      return
    }

    if (outcome.ok) {
      // 必须有抓拍帧才算成功；没拍到则按清晰度问题继续重拍
      if (!bestBlob) {
        if (attempt >= maxAttempts) {
          failAfterMaxAttempts()
          return
        }
        setSessionStatus(session, FACE_SESSION_STATUS.RECOGNIZING, {
          reason: 'quality',
          result: null
        })
        try {
          await delay(retryPauseMs, signal)
        } catch (e) {
          abortToCancel()
          return
        }
        continue
      }
      setSessionStatus(session, FACE_SESSION_STATUS.READY, {
        reason: null,
        result: {
          nurseId: outcome.nurseId,
          nurseName: outcome.nurseName,
          nurseAvatar: bestBlob,
          mock: Boolean(outcome.mock)
        }
      })
      video.srcObject = null
      releaseSessionStream(session)
      return
    }

    const reason = outcome.reason || 'mismatch'
    if (FACE_RECOVERABLE_REASONS[reason]) {
      if (attempt >= maxAttempts) {
        failAfterMaxAttempts()
        return
      }
      setSessionStatus(session, FACE_SESSION_STATUS.RECOGNIZING, {
        reason,
        result: null
      })
      try {
        await delay(retryPauseMs, signal)
      } catch (e) {
        abortToCancel()
        return
      }
      continue
    }

    setSessionStatus(session, FACE_SESSION_STATUS.FAILED, { reason, result: null })
    video.srcObject = null
    releaseSessionStream(session)
    return
  }

  releaseSessionStream(session)
  video.srcObject = null
}

/**
 * 打开去向盘时调用：启动（或重启）本机摄像头识别会话
 * @param {string} cellKey
 */
export function startFaceSession (cellKey) {
  if (!cellKey) return

  const prev = sessions.get(cellKey)
  // UI listeners 迁移到新会话，避免开盘重启后状态条断订
  const prevListeners = prev && prev.listeners.length
    ? prev.listeners.splice(0, prev.listeners.length)
    : []
  if (prev) {
    prev.abortController.abort()
    stopStream(prev.stream)
    prev.stream = null
    // 仅通知 waiters（合流短等）；listeners 已迁走，避免闪一帧 cancelled
    prev.status = FACE_SESSION_STATUS.CANCELLED
    prev.reason = 'cancelled'
    prev.result = null
    notifyWaiters(prev)
  }

  const session = createIdleSession(cellKey)
  session.generation = (prev?.generation || 0) + 1
  session.abortController = new AbortController()
  session.listeners = prevListeners
  sessions.set(cellKey, session)
  // 重新拍必须先清掉旧识别结果，否则 UI 仍会按 READY 放开「取消登记」等操作
  setSessionStatus(session, FACE_SESSION_STATUS.RECOGNIZING, {
    reason: null,
    result: null
  })

  runCapturePipeline(session).catch(() => {
    if (sessions.get(cellKey) !== session) return
    if (
      session.status === FACE_SESSION_STATUS.READY
      || session.status === FACE_SESSION_STATUS.FAILED
      || session.status === FACE_SESSION_STATUS.CANCELLED
    ) {
      return
    }
    setSessionStatus(session, FACE_SESSION_STATUS.FAILED, { reason: 'device', result: null })
  })
}

/**
 * 关盘 / 放弃：取消会话并停流
 * @param {string} cellKey
 */
export function cancelFaceSession (cellKey) {
  const session = sessions.get(cellKey)
  if (!session) return
  session.abortController.abort()
  releaseSessionStream(session)
  setSessionStatus(session, FACE_SESSION_STATUS.CANCELLED, { reason: 'cancelled', result: null })
}

/**
 * 提交成功后释放（与 cancel 相同停流，语义区分）
 * @param {string} cellKey
 */
export function stopFaceSession (cellKey) {
  cancelFaceSession(cellKey)
}

/**
 * @param {string} cellKey
 * @returns {string}
 */
export function getFaceSessionStatus (cellKey) {
  return sessions.get(cellKey)?.status || FACE_SESSION_STATUS.IDLE
}

/**
 * @param {string} cellKey
 * @returns {FaceSessionSnapshot}
 */
export function getFaceSessionSnapshot (cellKey) {
  const session = sessions.get(cellKey)
  return session ? toSnapshot(session) : idleSnapshot()
}

/**
 * 取景预览用：本机摄像头 MediaStream（与萤石监控流无关）
 * @param {string} cellKey
 * @returns {MediaStream|null}
 */
export function getFaceSessionStream (cellKey) {
  return sessions.get(cellKey)?.stream || null
}

/**
 * 订阅会话状态（用于去向盘身份感知条）。订阅时立即推送当前快照。
 * 重启会话时 listeners 会迁移到新 session，无需重新订阅。
 * @param {string} cellKey
 * @param {(snap: FaceSessionSnapshot) => void} onChange
 * @returns {() => void} unsubscribe
 */
export function subscribeFaceSession (cellKey, onChange) {
  if (!cellKey || typeof onChange !== 'function') {
    return () => {}
  }

  let session = sessions.get(cellKey)
  if (!session) {
    session = createIdleSession(cellKey)
    sessions.set(cellKey, session)
  }
  session.listeners.push(onChange)
  try {
    onChange(toSnapshot(session))
  } catch (e) {
    /* ignore */
  }

  return () => {
    const current = sessions.get(cellKey)
    if (!current) return
    const idx = current.listeners.indexOf(onChange)
    if (idx >= 0) current.listeners.splice(idx, 1)
  }
}

/**
 * 选去向时合流：已 ready 立即返回；recognizing 则短等
 * @param {string} cellKey
 * @param {{ timeoutMs?: number }} [options]
 * @returns {Promise<
 *   | ({ ok: true } & FaceVerifyResult)
 *   | { ok: false, reason: string }
 * >}
 */
export function awaitFaceResult (cellKey, options = {}) {
  const timeoutMs = options.timeoutMs != null
    ? options.timeoutMs
    : FACE_CAPTURE_CONFIG.awaitMergeMs

  const session = sessions.get(cellKey)
  if (session && session.status === FACE_SESSION_STATUS.READY && session.result) {
    return Promise.resolve({ ok: true, ...session.result })
  }
  if (FACE_RESULT_ALWAYS_OK) {
    return Promise.resolve(testPassFaceResult())
  }
  if (!session) {
    return Promise.resolve({ ok: false, reason: 'cancelled' })
  }

  if (
    session.status === FACE_SESSION_STATUS.FAILED
    || session.status === FACE_SESSION_STATUS.CANCELLED
  ) {
    return Promise.resolve({ ok: false, reason: session.reason || session.status })
  }

  return new Promise((resolve) => {
    let settled = false
    const finish = (value) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      resolve(value)
    }

    const onUpdate = (s) => {
      if (s.status === FACE_SESSION_STATUS.READY && s.result) {
        finish({ ok: true, ...s.result })
        return
      }
      if (
        s.status === FACE_SESSION_STATUS.FAILED
        || s.status === FACE_SESSION_STATUS.CANCELLED
      ) {
        finish({ ok: false, reason: s.reason || s.status })
      }
    }

    session.waiters.push(onUpdate)

    const timer = window.setTimeout(() => {
      // 超时前再读一次
      if (session.status === FACE_SESSION_STATUS.READY && session.result) {
        finish({ ok: true, ...session.result })
        return
      }
      if (
        session.status === FACE_SESSION_STATUS.FAILED
        || session.status === FACE_SESSION_STATUS.CANCELLED
      ) {
        finish({ ok: false, reason: session.reason || session.status })
        return
      }
      finish({ ok: false, reason: 'timeout' })
    }, timeoutMs)
  })
}

/**
 * 调试/演示：设置 mock 失败率
 * @param {number} rate 0～1
 */
export function setMockFaceFailRate (rate) {
  const n = Number(rate)
  FACE_CAPTURE_CONFIG.mockFailRate = !Number.isFinite(n)
    ? 0
    : Math.min(1, Math.max(0, n))
}

/**
 * 开关真识别（也可用 public/face-local.json 的 useMockRecognition）
 * @param {boolean} enabled true=mock；false=调 aegis-face
 */
export function setUseMockFaceRecognition (enabled) {
  FACE_CAPTURE_CONFIG.useMockRecognition = Boolean(enabled)
}
